import React, { Component } from 'react';
import {
  Animated,
  SafeAreaView,
  FlatList,
  StatusBar,
  Platform,
  StyleSheet,
  View,
  Dimensions
} from 'react-native';

import { isFunction as _isFunction, get as _get } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import ErrorScreen from '@vfgroup-oneplatform/foundation/Components/ErrorScreen';
import OverlayScreen from '@vfgroup-oneplatform/foundation/Components/OverlayScreen';
import {
  Types,
  Colors,
  Images,
  withTheme,
  LightThemeColors,
} from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../FrameworkTestKeys';

import { Dashboard, DashboardContainer } from './Dashboard';
import withDiscoverSnap from './Dashboard/withDiscoverSnap';
import { DetailsScreen, EverythingIsOkHeader, EIOHeaderContainer } from './EveryThingIsOk';
import styles, { STATUSBAR_THEME } from './Styles';
import { handleIsLoading } from './Utils';

function getStatusBarStyle(type, isDark) {
  return type === Types.PRIMARY && isDark ? STATUSBAR_THEME.LIGHT : STATUSBAR_THEME.DARK;
}
class DashboardFeature extends Component {
  constructor(props) {
    super(props);
    this.state = {
      previousIsModalVisible: undefined,
      isModalVisible: false,
      lastUpdateTime: new Date(),
      statusBarStyle: getStatusBarStyle(
        _get(props, 'EIOConfig.type', Types.SECONDARY),
        _get(props, 'theme.isDark', false),
      ),
      statusBarColor: Colors.transparent,
      rootViewIosYPosition: undefined,
      isDashboardOnTop: true,
      isEIOCollapsed: true,
      spaceType: 1,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { lastUpdateTime } = props;
    if (lastUpdateTime !== state.lastUpdateTime) {
      return {
        lastUpdateTime,
      };
    }

    const newStatusBarStyle = getStatusBarStyle(
      _get(props, 'EIOConfig.type', Types.SECONDARY),
      _get(props, 'theme.isDark', false),
    );

    if (!state.previousIsModalVisible && state.isModalVisible) {
      return {
        statusBarColor: Colors.transparent,
        statusBarStyle: STATUSBAR_THEME.LIGHT,
        previousIsModalVisible: true,
      };
    } else if (state.previousIsModalVisible && !state.isModalVisible) {
      return {
        statusBarStyle: newStatusBarStyle,
        previousIsModalVisible: false,
      };
    }

    const statusBarThemedColor = _get(props, 'theme.colors.statusBarColor', state.statusBarColor);

    if (newStatusBarStyle !== state.statusBarStyle && state.statusBarColor === Colors.transparent) {
      return {
        statusBarStyle: newStatusBarStyle,
      };
    } else if (
      newStatusBarStyle !== state.statusBarStyle &&
      state.statusBarColor !== statusBarThemedColor
    ) {
      return {
        statusBarStyle: newStatusBarStyle,
        statusBarColor: statusBarThemedColor,
      };
    } else if (
      newStatusBarStyle === state.statusBarStyle &&
      state.statusBarColor !== Colors.transparent &&
      state.statusBarColor !== statusBarThemedColor
    ) {
      return {
        statusBarStyle: _get(props, 'theme.isDark', false)
          ? STATUSBAR_THEME.LIGHT
          : STATUSBAR_THEME.DARK,
        statusBarColor: statusBarThemedColor,
      };
    }

    return null;
  }

  componentDidMount() {
    const { onRefresh, refreshInterval, lastUpdateTime } = this.props;
    lastUpdateTime && this.setState({ lastUpdateTime });
    if (_isFunction(onRefresh) && refreshInterval) {
      this.onRefreshInterval = setInterval(() => {
        onRefresh().then(isRefreshed => {
          isRefreshed && this.setLastUpdateTime();
        });
      }, refreshInterval);
    }

    if (this.rootView && Platform.OS === 'ios') {
      this.rootView.measureInWindow(this.onContainerViewMeasure);
    }
  }

  componentDidUpdate(previousProps, previousState) {
    if (this.rootView && Platform.OS === 'ios') {
      this.rootView.measureInWindow(this.onContainerViewMeasure);
    }

    if (previousState.isModalVisible && !this.state.isModalVisible && this.contentOffset) {
      this.onScroll({ nativeEvent: { contentOffset: this.contentOffset } });
    }
  }

  componentWillUnmount() {
    clearInterval(this.onRefreshInterval);
  }

  onScroll = ({ nativeEvent }) => {
    const { statusBarColor } = this.state;
    const { scrollViewProps, theme, withDiscoverSnapProps } = this.props;
    const { contentOffset } = nativeEvent;
    this.contentOffset = contentOffset;
    if (withDiscoverSnapProps.onScroll && _isFunction(withDiscoverSnapProps.onScroll)) {
      withDiscoverSnapProps.onScroll(nativeEvent);
    }
    if (scrollViewProps.setScrollOffset && _isFunction(scrollViewProps.setScrollOffset)) {
      scrollViewProps.setScrollOffset(contentOffset);
    }

    if (contentOffset.y === 0 && statusBarColor !== Colors.transparent) {
      this.setState({
        statusBarColor: Colors.transparent,
        statusBarStyle: getStatusBarStyle(
          _get(this.props, 'EIOConfig.type', Types.SECONDARY),
          _get(this.props, 'theme.isDark', false),
        ),
        isDashboardOnTop: true,
      });
    } else if (contentOffset.y > 0 && statusBarColor === Colors.transparent) {
      this.setState({
        statusBarColor: theme.colors.statusBarColor,
        statusBarStyle: theme.isDark ? STATUSBAR_THEME.LIGHT : STATUSBAR_THEME.DARK,
        isDashboardOnTop: false,
      });
    }
  };

  onContainerViewMeasure = (x, y) => {
    if (this.state.rootViewIosYPosition !== y) {
      this.setState({ rootViewIosYPosition: y });
    }
  };

  onChangeSpaceType = newSpaceType => {
    this.setState({ spaceType: newSpaceType });
  };

  onProceed = () => {
    return null;
  };
  getContainerStyle = (isAndroidDevice, withStatusBarBehaviour, isModalVisible, containerStyle) => {
    // There is no need to actually check on isAndroidDevice since StatusBar.currentHeight is only available in android, this is done for explicitly and readability only
    if (isAndroidDevice && withStatusBarBehaviour) {
      const modalRelatedStyle = isModalVisible
        ? {
          backgroundColor:
            !this.contentOffset || (this.contentOffset && this.contentOffset.y === 0)
              ? Colors.transparent
              : Colors.activeGrey,
        }
        : undefined;
      return StyleSheet.flatten([containerStyle, styles.androidContainerStyle, modalRelatedStyle]);
    }

    return containerStyle;
  };
  getIOSStatusBarViewStyle = (statusBarColor, isModalVisible) => {
    const modalRelatedStyle = isModalVisible
      ? {
        backgroundColor:
          !this.contentOffset || (this.contentOffset && this.contentOffset.y === 0)
            ? Colors.transparent
            : Colors.activeGrey,
        zIndex: 0,
      }
      : {
        backgroundColor: statusBarColor,
      };

    return StyleSheet.flatten([styles.iosStatusBarView, modalRelatedStyle]);
  };
  getContainerStyleMemoized = memoize(this.getContainerStyle);
  getIOSStatusBarViewStyleMemoized = memoize(this.getIOSStatusBarViewStyle);
  toggleModal = (isVisible = !this.state.isModalVisible) => {
    this.setState({ isModalVisible: isVisible });
  };
  setUpRef = component => {
    this.rootView = component;
  };
  setLastUpdateTime = () => {
    this.setState({ lastUpdateTime: new Date() });
  };

  renderDashboard = () => {
    const {
      componentList,
      dashboardAnimationStyles,
      dashboardData,
      handleOnPressCard,
      handleOnPressDiscoverItem,
      dashboardView,
      mainCardsShadow,
      isCurrentScreen,
      discoverLoadingError,
      ...dashboardExtraProps
    } = this.props.dashboardConfig;
    const { isLoading } = this.props.dashboardConfig
    const { isLoadingDiscover } = handleIsLoading(isLoading)
    const { images, onRefresh, refreshInterval, withDiscoverSnapProps, refreshTitleStatusKeys } =
      this.props;
    const { lastUpdateTime, isModalVisible, spaceType } = this.state;
    return (
      dashboardView || (
        <DashboardContainer
          setDiscoverRef={withDiscoverSnapProps.setDiscoverSectionRef}
          images={images}
          data={dashboardData}
          componentList={componentList}
          onPressCard={handleOnPressCard}
          onPressDiscoverListItem={handleOnPressDiscoverItem}
          onRefresh={onRefresh}
          lastUpdateTime={lastUpdateTime}
          setLastUpdateTime={this.setLastUpdateTime}
          hasShadow={mainCardsShadow}
          isBackground={isModalVisible}
          isCurrentScreen={isCurrentScreen}
          spaceType={spaceType}
          discoverLoadingError={discoverLoadingError}
          refreshTitleStatusKeys={refreshTitleStatusKeys}
          refreshInterval={refreshInterval}
          navigate={this.props.navigate}
          products={this.props.products}
          account={this.props.account}
          tobiURL={this.props.tobiURL}
          sideBar={this.props.sideBar}
          updateRef={this.props.updateRef}
          isLoadingDiscover={isLoadingDiscover}
          theme={this.props.theme}
          totalUsers={this.props.totalUsers}
          accountSubtitle={this.props.accountSubtitle}
          toggleAccountSwitch={this.props.toggleAccountSwitch}

          // setIsVideoVisible={this.props.setIsVideoVisible}
          {...dashboardExtraProps}
        />
      )
    );
  };

  renderErrorScreen = () => {
    return (
      <ErrorScreen
        backgroundStyle={styles.errorContainer}
        iconAccessibilityLabel={FrameworkTestKeys.DBErrorIcon}
        arrowAccessibilityLabel={FrameworkTestKeys.DBErrorArrow}
        descAccessibilityLabel={FrameworkTestKeys.DBErrorDescription}
        tryAgainAccessibilityLabel={FrameworkTestKeys.DBErrorTryAgainText}
        text="dashboard_loading_error_message"
        textButton="dashboard_loading_error_try_again_button"
        onPress={this.props.onDashboardFailedTryAgainPress}
        testID={FrameworkTestKeys.dashboardErrorScreen}
      />
    );
  };
  renderEIO = () => {
    const {
      everyThingIsOkAnimationStyles,
      type,
      onChange,
      userName,
      getLogoRef,
      EIOData,
      handelPressEIOHeader,
      EIOView,
      onExpantionChange,
      RenderComponent = null,
      ...EIOExtraProps
    } = this.props.EIOConfig;
    const { images, isAnimationFinished } = this.props;
    const { isDashboardOnTop } = this.state;
    const handleExpantionChange = isEIOCollapsed => {
      this.setState({ isEIOCollapsed });
      onExpantionChange(isEIOCollapsed);
    };
    return (
      EIOView || (
        <Animated.View
          key={0}
          style={everyThingIsOkAnimationStyles}
          testID={FrameworkTestKeys.dashboardEIOViewAnimatedView}
        >
          <EIOHeaderContainer
            type={type}
            onChange={onChange}
            username={userName}
            getLogoRef={getLogoRef}
            data={EIOData}
            icons={images}
            onPress={handelPressEIOHeader}
            runAnimation={isAnimationFinished}
            onChangeSpaceType={this.onChangeSpaceType}
            expandable={isDashboardOnTop}
            onExpantionChange={handleExpantionChange}
            {...EIOExtraProps}
          />
          {RenderComponent && RenderComponent()}
        </Animated.View>
      )
    );
  };
  render() {
    const { statusBarStyle, statusBarColor, rootViewIosYPosition, isModalVisible, isEIOCollapsed } =
      this.state;
    const {
      withStatusBarBehaviour,
      containerStyle,
      scrollViewProps,
      isFullError,
      isDashboardFailed,
      withDiscoverSnapProps,
      withDisablingPointerEvents,
    } = this.props;

    const { isLoading } = this.props.dashboardConfig;
    const { isLoadingDiscover } = handleIsLoading(isLoading);

    if (!isFullError) {
      return (
        <View
          testID={FrameworkTestKeys.dashboardLoadingView}
          {...(withDisablingPointerEvents && {
            pointerEvents: isLoadingDiscover ? 'none' : 'auto',
          })}
        >
          {isDashboardFailed ? (
            <FlatList
              {...scrollViewProps}
              style={styles.flatListStyle(isEIOCollapsed)}
              data={[this.renderEIO(), this.renderErrorScreen()]}
              bounces={false}
              renderItem={({ item }) => item}
              testID={FrameworkTestKeys.dashboardFailedFlatList}
            />
          ) : (
            <FlatList
              ref={withDiscoverSnapProps.setFlatListRef}
              scrollEnabled={isEIOCollapsed}
              {...scrollViewProps}
              bounces={false}
              showsVerticalScrollIndicator={false}
              // style={styles.flatListStyle(isEIOCollapsed)}
              data={[this.renderEIO(), this.renderDashboard()]}
              renderItem={({ item }) => item}
              onScroll={this.onScroll}
              scrollEventThrottle={16}
              // ListFooterComponent={() => <View style={styles.footer} />}
              onMomentumScrollBegin={withDiscoverSnapProps.onMomentumScrollBegin}
              onTouchStart={withDiscoverSnapProps.onTouchStart}
              onMomentumScrollEnd={withDiscoverSnapProps.onMomentumScrollEnd}
              onScrollEndDrag={withDiscoverSnapProps.onScrollEndDrag}
              testID={FrameworkTestKeys.DashboardMainScroll}
            />
          )}
        </View>
      );
    } else {
      return (
        <>
          {withStatusBarBehaviour ? (
            <StatusBar barStyle={statusBarStyle} backgroundColor={statusBarColor} translucent />
          ) : null}
          <OverlayScreen
            okButtonText={'app_error_button'}
            headerKey={'app_error_title'}
            descriptionKey={'app_error_description'}
            visible
            centerImage={Images.ic_WarningHiLight_Theme}
            imageStyle={styles.overlayImage}
            hasSingleButton
            closeIcon={Images.ic_overlay_close}
            onProceed={this.onProceed}
            iconAccessabilityLabel="warningIcon"
            titleAccessabilityLabel="errorTitle"
            descriptionAccessabilityLabel="errorDesc"
            primaryBtnAccessabilityLabel="actionButton"
            testID={FrameworkTestKeys.dashboardOverlayScreen}
          />
        </>
      );
    }
  }
}

DashboardFeature.defaultProps = {
  isAnimationFinished: true,
  withStatusBarBehaviour: true,
  scrollViewProps: {},
  withDisablingPointerEvents: true,
  theme: LightThemeColors,
};

DashboardFeature.propTypes = {
  withDiscoverSnapProps: PropTypes.object,
  lastUpdateTime: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.number,
    PropTypes.string,
  ]),
  withDisablingPointerEvents: PropTypes.bool,
  EIOConfig: PropTypes.object,
  dashboardConfig: PropTypes.object,
  containerStyle: PropTypes.object,
  images: PropTypes.object,
  isAnimationFinished: PropTypes.bool,
  onRefresh: PropTypes.func,
  refreshInterval: PropTypes.number,
  withStatusBarBehaviour: PropTypes.bool,
  isFullError: PropTypes.bool,
  isDashboardFailed: PropTypes.bool,
  onDashboardFailedTryAgainPress: PropTypes.func,
  scrollViewProps: PropTypes.object,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  refreshTitleStatusKeys: PropTypes.shape({
    updatedNow: PropTypes.string,
    sinceLastUpdate: PropTypes.string,
  }),
};

export {
  EverythingIsOkHeader,
  EIOHeaderContainer,
  DetailsScreen,
  Dashboard,
  DashboardContainer,
  DashboardFeature,
};

export * from './Tray';
export default withDiscoverSnap(withTheme(DashboardFeature));
